// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { NotificationConfigurationList } from '@/audit/api/types';
import { toAuditedMessageTypeLabel } from '@/constants/audited-message-type';
import { getNotificationChannelLabel } from '@/constants/notification-channel';
import { Stack, Typography } from '@mui/material';
import { uniq, without } from 'lodash-es';

const LIST_FORMATTER = new Intl.ListFormat(undefined, {
  type: 'conjunction',
  style: 'long',
});

interface Props {
  property: NotificationConfigurationList;
}

export function AuditNotificationConfigurationPropertyList(props: Props) {
  const topics = uniq([
    ...props.property.oldValue.map(({ topic }) => topic),
    ...props.property.newValue.map(({ topic }) => topic),
  ]);

  const updates = topics.map((topic) => {
    const oldValue = props.property.oldValue.find(
      (value) => value.topic === topic
    );

    const newValue = props.property.newValue.find(
      (value) => value.topic === topic
    );

    const oldChannels = oldValue?.channels ?? [];
    const newChannels = newValue?.channels ?? [];
    return {
      topic,
      oldChannels: without(oldChannels, ...newChannels),
      newChannels: without(newChannels, ...oldChannels),
    };
  });

  return (
    <Stack role="list">
      {updates
        .filter((update) => {
          if (
            update.oldChannels.length == 0 &&
            update.newChannels.length == 0
          ) {
            // Is equal
            return false;
          }
          if (update.oldChannels.length !== update.newChannels.length) {
            // Not equal, so keep them
            return true;
          }
          // Check to see if they are equal
          return !update.newChannels.every((val, index) => {
            return val === update.oldChannels[index];
          });
        })
        .map((update) => {
          const newChannelLabels = update.newChannels.map(
            getNotificationChannelLabel
          );

          const oldChannelLabels = update.oldChannels.map(
            getNotificationChannelLabel
          );

          const showSeparator =
            update.oldChannels.length > 0 && update.newChannels.length > 0;

          return (
            <Typography key={update.topic} role="listitem" variant="caption">
              <b>{toAuditedMessageTypeLabel(update.topic)}</b>:{' '}
              {update.newChannels.length > 0 && (
                <Typography component="span" variant="inherit">
                  subscribed via{' '}
                  <b>{LIST_FORMATTER.format(newChannelLabels)}</b>
                </Typography>
              )}
              {showSeparator && '; '}
              {update.oldChannels.length > 0 && (
                <Typography component="span" variant="inherit">
                  unsubscribed via{' '}
                  <b>{LIST_FORMATTER.format(oldChannelLabels)}</b>
                </Typography>
              )}
            </Typography>
          );
        })}
    </Stack>
  );
}
